/**
 * 票務數據統計報表（GET /admin/reports/*）。
 */

import { apiFetch, buildQueryString, rawFetch } from './core';

export type ReportGranularity = 'day' | 'week' | 'month';
export type BreakdownDimension = 'product' | 'channel' | 'schedule' | 'ticket_type';

export interface RevenueTrendRow {
  period: string;
  order_count: number;
  gross_sales: string;
  refund_count: number;
  refund_amount: string;
  net_sales: string;
}

export interface RevenueTrendResponse {
  items: RevenueTrendRow[];
  granularity: ReportGranularity;
  from: string;
  to: string;
}

export interface SalesBreakdownRow {
  key: string;
  label: string;
  order_count: number;
  ticket_count: number;
  gross_sales: string;
  share: number;
}

export interface SalesBreakdownResponse {
  items: SalesBreakdownRow[];
  dimension: BreakdownDimension;
  from: string;
  to: string;
}

export interface RefundCheckinSummary {
  from: string;
  to: string;
  paid_orders: number;
  tickets_issued: number;
  refund_count: number;
  refund_amount: string;
  refund_rate: number;
  admitted_count: number;
  boarding_rate: number;
  rejected_count: number;
  no_show_count: number;
}

export function getRevenueTrend(q: {
  from: string;
  to: string;
  granularity: ReportGranularity;
}): Promise<RevenueTrendResponse> {
  const qs = buildQueryString({ from: q.from, to: q.to, granularity: q.granularity });
  return apiFetch<RevenueTrendResponse>(`/admin/reports/revenue-trend${qs}`, { method: 'GET' });
}

export function getSalesBreakdown(q: {
  from: string;
  to: string;
  dimension: BreakdownDimension;
}): Promise<SalesBreakdownResponse> {
  const qs = buildQueryString({ from: q.from, to: q.to, dimension: q.dimension });
  return apiFetch<SalesBreakdownResponse>(`/admin/reports/sales-breakdown${qs}`, { method: 'GET' });
}

export function getRefundCheckinSummary(q: {
  from: string;
  to: string;
}): Promise<RefundCheckinSummary> {
  const qs = buildQueryString({ from: q.from, to: q.to });
  return apiFetch<RefundCheckinSummary>(`/admin/reports/refund-checkin${qs}`, { method: 'GET' });
}

/**
 * 下載報表 CSV（帶 Bearer；以 blob 觸發瀏覽器下載）。
 * path 例：`/admin/reports/revenue-trend`；query 內含 from/to 等（不含 format）。
 */
export async function downloadReportCsv(
  path: string,
  query: Record<string, string>,
  filename: string,
): Promise<void> {
  const qs = buildQueryString({ ...query, format: 'csv' });
  const res = await rawFetch(`${path}${qs}`, { method: 'GET' });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
